import React, { Component } from "react";
import { connect } from "react-redux";
import { WidthProvider, Responsive } from "react-grid-layout";
import * as _ from "lodash";
import Axios from "axios";
import jwt from "jsonwebtoken";
import moment from "moment";
import { Spin } from "antd";
import BoardDataTableContainer from "./BoardDataTableContainer";
import { returnDataArrayObject } from "../../../utils/arrayHelper";
import BoardChartContainer from "./BoardChartContainer";
import './HtmlReportWidgets.css'
import "../../../styles/boardWidgets.css";
const ResponsiveReactGridLayout = WidthProvider(Responsive);

class HtmlReportWidgets extends Component {

  /* Same grid units as the editor canvas and the dashboard view - see the note on
     ReportWidjets.defaultProps. A report holds a single widget, so the column count
     rarely showed, but the row height did: 40px rows drew it a third taller than it was
     laid out as. (Was: rowHeight 40, cols { lg: 12, md: 6, sm: 6, xs: 4, xxs: 2 }.) */
  static defaultProps = {
    className: "reportview-layout",
    rowHeight: 30,
    cols: { lg: 12 },
    // verticalCompact: false out-ranks compactType in RGL - left out for that reason
    preventCollision: true,
  };

  constructor(props) {
    super(props);

    this.state = {
      layouts: null,
      reportJoinData: [],
      fieldsToShow: [],
      updateCount: 0,

    };
  }

  /**
   * Keeps only the rows this drill-down asked for.
   *
   * The report loads its data exactly as it normally would, then this trims the rows -
   * no extra request, and it works whether the resource is a GET or a POST. Sending the
   * value to the API instead is unreliable here: for JWT resources the request only
   * fires for POST-with-body-params, and in the non-JWT branch the body is assigned to
   * `httpConfig.body`, which Axios ignores.
   *
   * Only params marked `clientFilter` are applied - those are the ones built from the
   * column -> target field pair. Older boards pass parameterKeys keyed by their own
   * column names, which are meaningless in the target report, so they are left alone.
   */
  applyDrillDownFilter = (rawData) => {
    const params = (this.props.drillDownParams || []).filter((p) => p && p.clientFilter && p.key);
    if (!params.length) return rawData;
    if (!rawData || !Array.isArray(rawData.data) || !rawData.data.length) return rawData;

    const matches = (row, param) => {
      // a widget that does not carry this field is not what the filter is about, so it
      // is left as it is rather than emptied
      if (!row || !(param.key in row)) return true;
      return String(row[param.key] ?? "").trim() === String(param.value ?? "").trim();
    };

    const firstRow = rawData.data[0];
    const applicable = params.filter((p) => firstRow && p.key in firstRow);
    if (!applicable.length) return rawData;

    return { ...rawData, data: rawData.data.filter((row) => applicable.every((p) => matches(row, p))) };
  };

  /* A report holds a single widget, so the thing that identifies it is the report itself.
     Dashboards hold several, so each keeps its own widget name. Same rule in the editor
     and wherever the board is embedded. */
  widgetTitleFor = (resource) => {
    const board = this.props.board;
    if (board?.configDetails?.boardType === "reports") return board?.name || "";
    return resource?.widgetName ? resource.widgetName : resource?.connectionData?.name || "";
  };

  /* Which action buttons this widget's header shows — same rule as the board view.
     A report's single widget is described by the board's own properties, so nothing is
     rendered on the widget itself; a dashboard's widgets each answer for themselves. */
  /* Whether the widget draws a header bar at all - same rule as the board view: this page
     is titled with the report's name, so a bar on the widget could only repeat it (and on
     a report it carries no buttons either, per the properties below). */
  isReportBoard = () => this.props.board?.configDetails?.boardType === "reports";

  widgetPropertiesFor = (resource) => {
    if (this.props.board?.configDetails?.boardType === "reports") return {};
    const properties = resource?.properties || {};
    return { csvdownload: Boolean(properties.csvdownload), fullscreen: Boolean(properties.fullscreen) };
  };

  componentDidMount() {
    if (this.props.board && this.props.board.resources) {
      this.fetchReport();
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (JSON.stringify(prevProps.board) !== JSON.stringify(this.props.board)) {
      // this.fetchReport();
    }

    /* Ready means EVERY widget has its rows, not just the first one - layouts is set as
       soon as one resource resolves, so firing then would drop the caller's spinner while
       the rest of the report was still blank. Callers keep a timeout in case a request
       never comes back. */
    if (!this.reportReadyNotified && this.state.layouts && this.state.layouts.length) {
      const allLoaded = this.state.layouts.every((el) => el && el.reportData);
      if (allLoaded) {
        this.reportReadyNotified = true;
        if (this.props.onReportReady) this.props.onReportReady();
      }
    }
  }

  fetchReport = async () => {
    if (this.props.board && this.props.board.resources) {
      let noOfResources = this.props.board['resources'].length
      let layoutData = this.props.board.resources.map(function (j, key) {
        return {
          i: j?.layout?.i || 0,
          x: j?.layout?.x || 0,
          y: j?.layout?.y || 0,
          w: j?.layout?.w || 4,
          h: 18,//noOfResources>1? j?.layout?.h : 12,
          widgetType: j.charts && Boolean(Object.values(j.charts).length) ? "chart" : "table",
          chartConfig: j?.charts || null,
          resourceId: j?.resourceId || null,
        };
      });
      this.props.board.resources &&
        this.props.board.resources.forEach(async (resource, idx) => {
          if (
            resource.connectionData &&
            resource.connectionData &&
            resource.connectionData.sources &&
            resource.connectionData.sources.length > 0
          ) {
            layoutData[idx].reportData = {
              rawData: this.applyDrillDownFilter({ data: resource.reportData }),
              fieldsToShow: resource.availableFields,
              aggregation: resource.aggregation
                ? resource.aggregation
                : null,
              aggRowBuilders: resource.aggRowBuilders
                ? resource.aggRowBuilders
                : null,
              aggGroupBuilders: resource.aggGroupBuilders
                ? resource.aggGroupBuilders
                : null,
              groupedReportData: resource.groupedReportData
                ? resource.groupedReportData
                : null,
              widgetName: this.widgetTitleFor(resource),
              // per-widget display properties (download / full screen) - board-level on a report
              widgetProperties: this.widgetPropertiesFor(resource),
              connectionData: resource.connectionData,
              drillDowns: resource.drillDowns ? resource.drillDowns : null
            };
            this.setState({
              fieldsToShow: [
                ...this.state.fieldsToShow,
                resource.availableFields,
              ],
              layouts: layoutData,
            });
          } else {
            if (
              resource.connectionData &&
              resource.connectionData &&
              resource.connectionData.resoueceType === "Rest API"
            ) {
              // const response = await axios.get(resource.connectionData.baseUrl);
              const requestValues =
                resource.connectionData.connectionData.requestValues;
              const authInfo = resource.connectionData.connectionData.authInfo;
              const customFilters = resource.customFilters
                ? resource.customFilters
                : null;

              let urlParamSring = "";
              if (requestValues.urlParams.length > 0) {
                urlParamSring = urlParamSring + "?";
                for (let i = 0; i < requestValues.urlParams.length; i++) {
                  if (
                    requestValues.urlParams[i]["key"] !== "" &&
                    requestValues.urlParams[i]["value"] !== ""
                  ) {
                    urlParamSring =
                      urlParamSring +
                      requestValues.urlParams[i]["key"] +
                      "=" +
                      requestValues.urlParams[i]["value"] +
                      "&";
                  }
                }
                urlParamSring.slice(0, -1);
              }

              let headerObj = {};
              if (requestValues.requestHeaders.length > 0) {
                for (let i = 0; i < requestValues.requestHeaders.length; i++) {
                  if (
                    requestValues.requestHeaders[i]["key"] !== "" &&
                    requestValues.requestHeaders[i]["value"] !== ""
                  ) {
                    headerObj[requestValues.requestHeaders[i]["key"]] =
                      requestValues.requestHeaders[i]["value"];
                  }
                }
              }

              let bodyParamObj = {};
              // if (requestValues.authParams) {
              //     bodyParamObj = Object.assign({}, authParams);
              // }
              if (requestValues.bodyParams.length > 0) {
                // let bodyParamObj=this.props.bodyParams
                for (let i = 0; i < requestValues.bodyParams.length; i++) {
                  if (
                    requestValues.bodyParams[i]["key"] !== "" &&
                    requestValues.bodyParams[i]["value"] !== ""
                  ) {
                    bodyParamObj[requestValues.bodyParams[i]["key"]] = this.props.bodyParams ? this.props.bodyParams[requestValues.bodyParams[i]["key"]] : requestValues.bodyParams[i]["value"];
                  } else if (requestValues.bodyParams[i]["key"] !== "" && requestValues.bodyParams[i]["dataType"] === "date") {
                    if (requestValues.bodyParams[i]["dateConfig"]) {
                      let dateCf = requestValues.bodyParams[i]["dateConfig"];
                      if (dateCf.dateOption === "daysBefore") {
                        let dateVal = moment.utc().subtract(dateCf.timeUnits, 'day').startOf('day').format(dateCf.dateFormat);

                        if (this.props.bodyParam) {
                          if (this.props.bodyParams[requestValues.bodyParams[i]["key"]].includes('DaysBefore')) {
                            let days = Number(this.props.bodyParams[requestValues.bodyParams[i]["key"]].replace('DaysBefore', ''))
                            dateVal = moment.utc().subtract(days, 'day').startOf('day').format(dateCf.dateFormat);
                          } else if (this.props.bodyParams[requestValues.bodyParams[i]["key"]] === "Today") {
                            dateVal = moment().format(dateCf.dateFormat);
                          }
                        }
                        bodyParamObj[requestValues.bodyParams[i]["key"]] = dateVal;
                      } else if (dateCf.dateOption === "today") {
                        let dateVal = moment().format(dateCf.dateFormat);
                        bodyParamObj[requestValues.bodyParams[i]["key"]] = dateVal;
                      }
                    }
                  }
                }


              }
              if(this.props['drillDownParams']) {
                for (let i = 0; i < this.props['drillDownParams'].length; i++) {
                  bodyParamObj[this.props['drillDownParams'][i]['key']] = this.props['drillDownParams'][i]['value']
                }
              }

              if (customFilters && customFilters.length > 0) {
                customFilters.forEach((element) => {
                  if (element.parameterName !== "") {
                    if (element.parameterType === "query") {
                      if (urlParamSring === "") {
                        urlParamSring = urlParamSring + "?";
                      }
                      urlParamSring =
                        urlParamSring +
                        element.parameterName +
                        "=" +
                        element.value +
                        "&";
                    } else {
                      // bodyParamObj=this.props.bodyParams
                      if (element.value) {
                        bodyParamObj[element.parameterName] = element.value;
                      }
                    }
                  }
                });
              }
              // // await Axios.get(resource.connectionData.baseUrl)

              if (authInfo.method === "jwt") {
                console.log("JWT PROCESS");
                const timestamp = Math.floor(Date.now() / 1000) + 1000;
                const privateKey = authInfo.parameters.privateKey;
                let userToken = localStorage.getItem('accessToken');
                var payload = {
                  aud: authInfo.parameters.aud,
                  exp: timestamp,
                  iss: authInfo.parameters.iss,
                  userToken: userToken
                };
                var signOptions = {
                  algorithm: "RS256",
                };

                var privateKeyVal = privateKey
                  ? privateKey.replace(/\\n/g, "\n")
                  : null;

                if (privateKeyVal) {
                  try {
                    var token = jwt.sign(payload, privateKeyVal, signOptions);
                    console.log("Generated JWT authParams=====>>", token);
                    // setApiTestStatus("success")
                    let httpConfig = null;

                    if (
                      requestValues.bodyParams.length > 0 &&
                      resource.connectionData.connectionData.requestValues
                        .httpMethod === "post"
                    ) {
                      httpConfig = {
                        method: "post",
                        // url: resource.connectionData.connectionData.baseUrl + urlParamSring,
                        headers: {
                          "Access-Control-Allow-Origin": "*",
                          "Content-Type": "application/json",
                          Authorization: `Bearer ` + token,
                        },
                        body: JSON.stringify(bodyParamObj),
                      };
                      await fetch(
                        resource.connectionData.connectionData.baseUrl +
                        urlParamSring,
                        httpConfig
                      )
                        .then((data) => data.json())
                        .then((response) => {
                          console.log(
                            "=====================response=========response==========>>>>>>>",
                            response
                          );
                          if (response) {
                            layoutData[idx].reportData = {
                              rawData: response
                                ? this.applyDrillDownFilter(returnDataArrayObject(response))
                                : null,
                              fieldsToShow: resource.availableFields
                                ? resource.availableFields
                                : null,
                              aggregation: resource.aggregation
                                ? resource.aggregation
                                : null,
                              aggRowBuilders: resource.aggRowBuilders
                                ? resource.aggRowBuilders
                                : null,
                              aggGroupBuilders: resource.aggGroupBuilders
                                ? resource.aggGroupBuilders
                                : null,
                              groupedReportData: resource.groupedReportData && Boolean(resource.groupedReportData.length)
                                ? resource.groupedReportData
                                : null,
                              widgetName: this.widgetTitleFor(resource),
                              // per-widget display properties (download / full screen) - board-level on a report
                              widgetProperties: this.widgetPropertiesFor(resource),
                              connectionData: resource.connectionData,
                              drillDowns: resource.drillDowns ? resource.drillDowns : null
                            };
                            console.log("layoutData===>>", layoutData);
                            this.setState({
                              fieldsToShow: [
                                ...this.state.fieldsToShow,
                                resource.availableFields,
                              ],
                              layouts: layoutData,
                            });
                          }
                        })
                        .catch((error) => {
                          console.log(error);
                        });
                    }
                  } catch (e) {
                    console.log("JWT authPsign error=====>>", e);
                  }
                }
              } else {
                console.log("Report fetch=====>>");
                let httpConfig = {
                  method:
                    resource.connectionData.connectionData.requestValues
                      .httpMethod,
                  url:
                    resource.connectionData.connectionData.baseUrl +
                    urlParamSring,
                };
                if (requestValues.bodyParams.length > 0) {
                  httpConfig.body = bodyParamObj;
                }
                if (requestValues.requestHeaders.length > 0) {
                  httpConfig.headers = headerObj;
                }

                await Axios(httpConfig)
                  .then(async (response) => {
                    console.log("Report resp=====>>", response);
                    // rowDataArr.push(returnDataArrayObject(response.data)
                    layoutData[idx].reportData = {
                      rawData: this.applyDrillDownFilter(returnDataArrayObject(response.data)),
                      fieldsToShow: resource.availableFields,
                      aggregation: resource.aggregation
                        ? resource.aggregation
                        : null,
                      aggRowBuilders: resource.aggRowBuilders
                        ? resource.aggRowBuilders
                        : null,
                      aggGroupBuilders: resource.aggGroupBuilders
                        ? resource.aggGroupBuilders
                        : null,
                      groupedReportData: resource.groupedReportData
                        ? resource.groupedReportData
                        : null,
                      widgetName: this.widgetTitleFor(resource),
                      // per-widget display properties (download / full screen) - board-level on a report
                      widgetProperties: this.widgetPropertiesFor(resource),
                      connectionData: resource.connectionData,
                      drillDowns: resource.drillDowns ? resource.drillDowns : null
                    };
                    this.setState({
                      fieldsToShow: [
                        ...this.state.fieldsToShow,
                        resource.availableFields,
                      ],
                      layouts: layoutData,
                    });

                    // setReportDataStatus("success")
                    // setReportDataObject(response.data ? response.data : response)
                  })
                  .catch((error) => {
                    // setReportDataStatus("error")
                    // setReportDataObject(null)
                    console.log(error);
                  });
              }
            }
          }
        });
    }
  };

  fecthMultiResReport = async (resource, index) => {
    let dataList = [];
    resource.connectionData.sources.forEach((src) => {
      let layoutData = this.props.board.resources.map(function (j, key) {
        return {
          i: j.layout && j.layout.i ? j.layout.i : 0,
          x: j.layout && j.layout.x ? j.layout.x : 0,
          y: j.layout ? j.layout.y : 0,
          w: j.layout ? j.layout.w : 4,
          h: 12,//j.layout ? j.layout.h : 6,
          widgetType: j.charts && Boolean(Object.values(j.charts).length) ? "chart" : "table",
        };
      });

      layoutData[index].reportData = {
        rawData: this.applyDrillDownFilter(resource.reportData),
        fieldsToShow: resource.availableFields
          ? resource.availableFields
          : null,
        aggregation: resource.aggregation
          ? resource.aggregation
          : null,
        aggRowBuilders: resource.aggRowBuilders
          ? resource.aggRowBuilders
          : null,
        aggGroupBuilders: resource.aggGroupBuilders
          ? resource.aggGroupBuilders
          : null,
        groupedReportData: resource.groupedReportData
          ? resource.groupedReportData
          : null,
        widgetName: this.widgetTitleFor(resource),
        // per-widget display properties (download / full screen) - board-level on a report
        widgetProperties: this.widgetPropertiesFor(resource),
        connectionData: resource.connectionData,
        drillDowns: resource.drillDowns ? resource.drillDowns : null
      };

      this.setState({
        reportJoinData: [
          ...this.state.reportJoinData,
          {
            reportData: { data: resource.reportData },
            resourceId: src.id,
            name: src.name,
          },
        ],
        fieldsToShow: [
          ...this.state.fieldsToShow,
          resource.availableFields,
        ],
        layouts: layoutData,
      });
    });
  };

  fetchReportData = async () => {
    let rowDataArr = [];
  };

  generateDOM(el) {
    console.log("generateDOM EL===>>", el);
    const i = el.i;
    return (
      /* RGL writes this item's size and position inline from `data-grid`; the
         style={{ width: "100px !important" }} it used to carry is an invalid value React
         drops and RGL would overwrite anyway. */
      <div key={`dc_${i}`} data-grid={el}>
        {el.reportData &&
          el.reportData.rawData &&
          el.reportData.fieldsToShow ? (
          <>
            {el.widgetType === "table" ? (
              <BoardDataTableContainer
                // lets the empty state say "no matching records" instead of "no data"
                drillDownFiltered={(this.props.drillDownParams || []).some((p) => p && p.clientFilter && p.key)}
                reportData={el.reportData?.groupedReportData && Boolean(el.reportData?.groupedReportData.length)
                  ? { data: el.reportData.groupedReportData } : el.reportData.rawData}
                dataFilters={
                  this.props.selectedResource &&
                    this.props.selectedResource.dataFilters
                    ? this.props.selectedResource.dataFilters
                    : null
                }
                selectedResource={this.props.selectedResource}
                availableFields={el.reportData.fieldsToShow}
                aggregation={el.reportData.aggregation}
                aggGroupBuilders={el.reportData.aggGroupBuilders}
                aggRowBuilders={el.reportData.aggRowBuilders}
                widgetName={el.reportData.widgetName}
                widgetProperties={el.reportData.widgetProperties}
                /* the page header already says this - see isReportBoard. The name is
                   still passed through: it is the CSV file name. */
                hideHeader={this.isReportBoard()}
                createdAt={this.props.board.createdAt}
                updatedAt={this.props.board.updatedAt}
                connectionData={el.reportData.connectionData}
                drillDowns={el.reportData.drillDowns}
                resource={el}
              />
            ) : (
              <BoardChartContainer
                reportData={el.reportData.rawData}
                dataFilters={
                  this.props.selectedResource &&
                    this.props.selectedResource.dataFilters
                    ? this.props.selectedResource.dataFilters
                    : null
                }
                selectedResource={this.props.selectedResource}
                availableFields={el.reportData.fieldsToShow}
                aggregation={el.reportData.aggregation}
                aggRowBuilders={el.reportData.aggRowBuilders}
                widgetName={el.reportData.widgetName}
                widgetProperties={el.reportData.widgetProperties}
                // same as the table above - the report's name is on the page header
                hideHeader={this.isReportBoard()}
                createdAt={this.props.board.createdAt}
                updatedAt={this.props.board.updatedAt}
                connectionData={el.reportData.connectionData}
                charts={el.chartConfig}
                resourceId={el.resourceId}
              />
            )}
          </>
        ) : (
          // fills the card and centres the spinner in it - see .widget-pending
          <div className="widget-pending">
            <Spin />
          </div>
        )}
      </div>
    );
  }

  render() {
    return (
      <div
        className={`board-widget-grid ${
          this.state.layouts && this.state.layouts.length > 1 ? "report-hyperlink-view" : "report-hyperlink-view-single"
        }`}
      >

        {this.state.layouts ? (
          <ResponsiveReactGridLayout
            /* Same grid as the editor canvas - see the note on the dashboard view's
               ResponsiveReactGridLayout. A report has one widget, but it is still laid
               out on the 12-column canvas, so it is read back the same way. The spread
               moved above the explicit props: defaultProps land in this.props, so
               spreading after them overwrote whatever was passed here. */
            {...this.props}
            breakpoints={{ lg: 0 }}
            cols={{ lg: 12 }}
            compactType="vertical"
            isDroppable={false}
            isResizable={false}
            isDraggable={false}
          >
            {_.map(this.state.layouts, (el, index) => this.generateDOM(el))}
          </ResponsiveReactGridLayout>
        ) : null}

      </div>
    );
  }
}

// const mapStateToProps = (state) => {
//     const { boards } = state;
//     return {
//         selectedBoard: boards.selectedBoard,
//     }
// }
// const mapDispatchToProps = (dispatch) => ({
//     getBoardByIdAsync: (data) => dispatch(getBoardByIdAsync(data)),
//     updateSelectedBoard: (data) => dispatch(updateSelectedBoard(data))
// })
export default connect(null, null)(HtmlReportWidgets);
